/* eslint-disable @typescript-eslint/member-ordering */
import has from 'lodash/has';
import set from 'lodash/set';
import { DocumentElement, ParagraphElement, TextElement, AlloyNode } from '@tencent/alloy-editor';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import {
    IConvertFromModelProps,
    IEditorDataProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import {
    IBodyStyle,
    IParagraphStyle,
    ITextBodyStyle,
    ITextRunStyle, IWrapperStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import {
    getFormatTime, getResiziable,
    setStyleForPositionedTextInParagraphList,
    // 这两个是原来 textrun 的
    combineTextrunStyle, judgeTextrunsIsEmpty,
    getParagraphList,
} from '../utils/utils';

import { combineParaStyles, fixParaStyle, convertParaStyles  } from '../utils/paragraph-style';
import {
    combineCharacter,
} from '@src/slide/domain/ui-model/element-modeles/property/text/utils/character-utils';
import {
    FONT_SCALE,
    HIGHLIGHT_COLOR,
    DEFAULT_RAW_FONTSIZE,
    SEARCH_COLOR,
} from '../constants';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { isVerticalDirection } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import editorCompareEngine from '../editor-data-compare';
import { SearchInfo } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';


/**
 * TextBody属性对应的存储位置
 */
export const BODY_STYLES_KEYS = [
    'paddingTop', 'paddingLeft', 'paddingRight', 'paddingBottom', 'whiteSpace',
];
export const WRAPPER_STYLES_KEYS = [
    'textDirection', 'alignItems', 'columnNumber', 'columnSpace',
];

const EMPTY_PARA = [{
    endParaRPr: {
        useEndParaStyle: true,
    },
    paragraphProps: {},
    textRuns: [{
        text: '',
    }],
}];

/**
 * editor model层，转换和存储editor数据
 */
export class EditorDataModel {
    public resizable: boolean;  // 是否自缩排
    public autoFitType; // 文本自适应类型
    public isVert: boolean; // 是否竖排
    public resizeLevel?: number;
    //  转换数据时，是否要存储rawData(为了转换回来)，只读状态不需要转换rawData
    public noSaveRawData = false;
    // 文本横竖排类型
    public textDirection: TextDirectionType;
    public model?: any;
    /**
     * 转换后的UI显示数据
     */
    public editorValue: DocumentElement;

    /**
     *  文本显示的数据
     */
    private paragraphList?: IParagraph[];
    private textBodyStyle?: ITextBodyStyle;

    /**
     * 文本动态数据
     */
    private slideNum?: number;
    private datetime?: string | Date;
    private searchInfo?: SearchInfo;
    private linkThemeColor?: string;

    private readonly triggerUpdate?;

    public constructor(data: IEditorDataProps) {
        this.paragraphList = data.paragraphList;
        this.textBodyStyle = data.textBodyStyle;
        this.model = data.model;

        this.slideNum = data.slideNum;
        this.datetime = data.datetime;
        this.searchInfo = data.searchInfo;

        this.resizeLevel = data.resizeLevel;
        this.parseTextBodyStyle(data.textBodyStyle);

        this.noSaveRawData = data.noSaveRawData || false;
        this.triggerUpdate = data.triggerUpdate;
        this.linkThemeColor = data.linkThemeColor;

        this.editorValue = this.doConvertFromTextModel(this.paragraphList);
    }

    /**
     * 返回当前存储的paragraphList
     */
    public getParagraphList(deepClone?: boolean): IParagraph[] {
        if (deepClone) {
            return this.getClonedParagraphList();
        }
        return this.paragraphList || [];
    }

    /**
     * 获取当前存储的textBodyStyle
     */
    public getTextBodyStyle(): ITextBodyStyle {
        return this.textBodyStyle || {};
    }

    public get wrapperStyle(): IWrapperStyle | undefined {
        return this.getTextBodyStyle()?.wrapperStyle;
    }

    public get bodyStyle(): IBodyStyle | undefined {
        return this.getTextBodyStyle()?.bodyStyle;
    }

    public getStyleList(): (IParagraphStyle & ITextRunStyle)[] {
        return [];
    }

    /**
     * 对比数据，检查是否需要刷新UI，是则同时更新数据。
     * @param data
     * @param needTriggerUpdate 是否触发UI更新回调
     * @param forceUpdate 是否强制更新替换
     * @return 是否需要更新
     */
    // eslint-disable-next-line complexity
    public setRawData(data: IEditorDataProps, needTriggerUpdate?: boolean, forceUpdate?: boolean): boolean {
        const { paragraphList = this.paragraphList, textBodyStyle = this.textBodyStyle,
            slideNum = this.slideNum, resizeLevel = this.checkResizeLevel(data),
            datetime = this.datetime, searchInfo = this.searchInfo,
            noSaveRawData = this.noSaveRawData, linkThemeColor } = data;
        let shouldUpdate = forceUpdate || false;
        let rawDataChange = false;
        if (noSaveRawData !== this.noSaveRawData) {
            this.noSaveRawData = noSaveRawData;
            shouldUpdate = true;
            rawDataChange = true;
        }
        if (shouldUpdate || !this.compare(textBodyStyle, this.textBodyStyle)) {
            this.textBodyStyle = deepCopy(textBodyStyle);
            this.parseTextBodyStyle(textBodyStyle);
            shouldUpdate = true;
        }
        if (shouldUpdate || this.needParagraphListUpdate(paragraphList, resizeLevel, slideNum, datetime, searchInfo)) {
            this.paragraphList = paragraphList;

            this.resizeLevel = resizeLevel;
            this.slideNum = slideNum;
            this.datetime = datetime;
            if (this.searchInfo && searchInfo) {
                this.searchInfo = { ...this.searchInfo, ...searchInfo };
            } else {
                this.searchInfo = searchInfo;
            }
            shouldUpdate = true;
        }
        if (shouldUpdate || (linkThemeColor && this.getLinkColorNeedUpdate(paragraphList, linkThemeColor))) {
            this.linkThemeColor = linkThemeColor;
            shouldUpdate = true;
        }
        if (shouldUpdate) {
            const fixParaList = this.setSearchInPara(paragraphList);
            this.editorValue = this.doConvertFromTextModel(fixParaList);
        }
        // 触发UI更新
        if (needTriggerUpdate) {
            this.triggerUpdate?.(this.editorValue, rawDataChange);
        }

        return shouldUpdate;
    }

    /**
     * diff下链接主题是否有变更
     * @param paragraphList
     * @param linkColor
     */
    public getLinkColorNeedUpdate(paragraphList?: IParagraph[], linkColor?: string): boolean {
        let flag = false;
        // const { paragraphList, linkColor } = nextProps;
        if (linkColor) {
            paragraphList?.forEach((para) => {
                const { textRuns } = para;
                textRuns?.forEach((textRun) => {
                    if (textRun.styles?.href && textRun.styles.href.type !== 'tx') {
                        if (this.linkThemeColor !== linkColor) {
                            this.linkThemeColor = linkColor;
                            flag = true;
                        }
                    }
                });
            });
        }

        return flag;
    }

    /**
     *  resizeLevel可能是undefined，特殊处理
     * @param data
     */
    private checkResizeLevel(data: IEditorDataProps) {
        if (!has(data, 'resizeLevel')) {
            return this.resizeLevel;
        }
        return data.resizeLevel;
    }

    public getEditorValue(): DocumentElement {
        return this.editorValue;
    }

    /**
     * UI调用这个方法进行转换
     * @param paragraphList
     */
    public doConvertFromTextModel(paragraphList?: IParagraph[]): DocumentElement {
        return EditorDataModel.convertFromTextModel({
            paragraphList: paragraphList || this.paragraphList,
            textBodyStyle: this.textBodyStyle,
            datetime: this.datetime,
            slideNum: this.slideNum,
            resizeLevel: this.resizeLevel,
            model: this.model,
        });
    }

    /**
     * 转换paragraphList为 DocumentElement , UI显示所需要的数据。
     * @param props 转换所需参数
     * @param config
     */
    public static convertFromTextModel(
        props: IConvertFromModelProps,
        config?: { noConvertBack?: boolean },
    ): DocumentElement {
        const { textBodyStyle } = props;
        const { noConvertBack } = config || {};
        const paragraphs = this.completeParagraphList(props);
        return DataTransform.toEditorValue({
            paragraphs,
            style: {
                autoFitType: textBodyStyle?.autoFitType,
                ...textBodyStyle?.bodyStyle,
                ...textBodyStyle?.wrapperStyle,
            },
        }, { noOriginData: noConvertBack });
    }

    public doConvertParagraphListToParagraphElements(paragraphList?: IParagraph[]): ParagraphElement[] {
        return EditorDataModel.convertParagraphListToParagraphElements({
            paragraphList: paragraphList || this.paragraphList,
            datetime: this.datetime,
            slideNum: this.slideNum,
            resizeLevel: this.resizeLevel,
            model: this.model,
        });
    }

    public static convertParagraphListToParagraphElements(props: Omit<IConvertFromModelProps, 'textBodyStyle'>, noConvertBack = false) {
        const paragraphs = this.completeParagraphList(props);
        return DataTransform.createParagraphElements(paragraphs, noConvertBack);
    }

    public static completeParagraphList(props: Omit<IConvertFromModelProps, 'textBodyStyle'>) {
        const { paragraphList, resizeLevel, datetime, slideNum, model } = props;
        // 数据转换给 editor 时深拷贝下，editor 内部会对数据进行 freeze 处理，如果不拷贝的话，业务层直接操作引用可能会报错
        const paragraphs = deepCopy(paragraphList?.length ? paragraphList : EMPTY_PARA as IParagraph[]);
        const noRawData = true; // 3.0普通的不需要再打标了
        return Array.from(paragraphs).map((paragraph: IParagraph) => {
            const level = paragraph?.paragraphProps?.level || 0;
            let paraStyle = combineParaStyles(
                paragraph.paragraphProps,
                noRawData,
            );
            const { endParaRPr } = paragraph;
            const isParaEmpty = judgeTextrunsIsEmpty(paragraph.textRuns);
            let leaves: {
                styles: any;
                text: any;
            }[];
            let maxFontSize: number;
            if (isParaEmpty) {
                leaves = [this.getEmptyTextrun(endParaRPr, resizeLevel, noRawData)];
                maxFontSize = endParaRPr?.fontSize || DEFAULT_RAW_FONTSIZE;
            } else {
                leaves = Array.from(paragraph.textRuns || []).map((run: any, i) => {
                    const styles: any = this.computeStyles(run, i, paragraph, resizeLevel, noRawData) || {};
                    const text = this.getRunText(run, slideNum, datetime as Date, model);

                    // 页眉页脚的fieldType和id转换进marks
                    if (run.type === 'field') {
                        styles.fieldType = run.fieldType;
                        styles.fieldId = run.fieldId;
                        styles['raw-fieldType'] = true;
                        styles['raw-fieldId'] = true;
                    }

                    return {
                        styles,
                        text,
                    };
                });
                const textRuns = paragraph.textRuns || [];
                maxFontSize = textRuns.reduce(
                    (max, run) => ((run.styles?.fontSize || 0) > max ? run.styles?.fontSize : max)
                    , textRuns[0].styles?.fontSize || 0,
                ) || DEFAULT_RAW_FONTSIZE;
            }
            paraStyle = fixParaStyle(paraStyle, maxFontSize);

            const endParaRPrData = this.getEndParaData(isParaEmpty, endParaRPr, leaves);
            if (endParaRPrData) paraStyle.data = { endParaRPrData };
            const character = combineCharacter(paragraph.paragraphCharacter);
            // 无论 character 是否显示，都需要转换 character 数据，避免往回转的时候拿不到数据
            paraStyle.character = character;
            paraStyle.level = level;  // 保存level
            return { textRuns: leaves, paragraphProps: paraStyle };
        });
    }

    /**
     * 保留继承数据进行转换
     * 1. 用于修改母版 TitleOrCtrTitlePl 和 BodyPl 时的特殊场景
     * 修改上述占位符，样式没有落盘到自身，而是 master xxStyleList 上，所以不用区分继承数据，都是属于 master 的
     * 2. 正常的文本保存也调用这个方法进行转换，其中段落需要包含继承的数据，因为在 3.0 中 ui-sdk 时会根据标识过滤出自身数据然后触发保存
     * textbodyStyle 不需要包含继承的数据，因为目前 textbodyStyle 是增量保存，在数据保存时只需要包含新增的数据，所以此处要做过滤
     * @param docElement
     * @returns
     */
    public doConvertFromDocElementKeepInheritData(
        docElement: DocumentElement,
        excludeParagraphInheritedData = false,
        excludeTextBodyInheritedData = true,
    ): { paragraphList: any[], textBodyStyle: ITextBodyStyle } {
        return EditorDataModel.convertFromDocElement(
            docElement,
            this.resizable,
            excludeParagraphInheritedData,
            excludeTextBodyInheritedData,
        );
    }

    /**
     * UI显示数据转换为文本model数据
     * @param docElement
     * @param resizable
     * @param excludeParagraphInheritedData 为 true 表示段落上只保留自身属性，false 会保留继承的属性
     * @param excludeTextBodyInheritedData 为 true 表示 textbody 上只保留自身属性，false 会保留继承的属性
     * @param keepThemeColor 为 true 表示需要保留主题色，否则需要改为具体色值
     */
    public static convertFromDocElement(
        docElement: DocumentElement,
        resizable = false,
        excludeParagraphInheritedData = true,
        excludeTextBodyInheritedData = true,
        keepThemeColor = true,
    ): { paragraphList: any[], textBodyStyle: ITextBodyStyle } {
        // editor 内部数据经过 freeze 处理，传回来时需要深拷贝，避免外部直接操作引用可能会报错
        const documentElement = deepCopy(docElement);
        const paragraphList: any[] = this.convertParagraphElementsToParagraphList(
            documentElement.children,
            resizable, excludeParagraphInheritedData, keepThemeColor,
        );
        const textBodyStyle = this.convertTextBodyStyle(documentElement, excludeTextBodyInheritedData);
        return { paragraphList, textBodyStyle };
    }

    public static convertParagraphElementsToParagraphList(
        paragraphElement: ParagraphElement[],
        resizable = false,
        excludeParagraphInheritedData = true,
        keepThemeColor = true,
    ) {
        return paragraphElement.map((paraEle: ParagraphElement) => {
            let endParaRPr;
            const filterFunction = excludeParagraphInheritedData
                // 需要保留自身属性，过滤出 element[`origin-raw-${key}`] 为 true 的属性
                ? (key: string, value: AlloyNode[keyof AlloyNode], element: AlloyNode) => element[`origin-raw-${key}`]
                // 如果要保留继承属性，那么 raw- 开头的标志也要保留，用于在 ui=>sdk 时筛选出自身属性用于保存
                : undefined;
            const textRuns = paraEle.children.map((textEle: TextElement, index) => {
                const styles: any = DataTransform.getOriginPropsWithFilter(textEle, filterFunction);
                const { text } = textEle;
                if (text === '\n') {
                    return {
                        type: 'runBr', props: { lang: 'en-US' }, text: '\n',
                    };
                }
                const run: any = {
                    text,
                    styles,
                    type: 'run',
                };
                // 页眉页脚属性转换回ui-model数据
                if (has(run.styles, 'fieldType')) {
                    run.type = 'field';
                    run.fieldType = run.styles.fieldType;
                    run.fieldId = run.styles.fieldId;
                }

                if (!keepThemeColor && run.styles.tdColor?.isSchemeColor) {
                    // 不需要保留主题色，那么把主题色改为具体色值
                    run.styles.tdColor = new TDColor({
                        rgbaStr: run.styles.tdColor.srgb_color,
                    });
                }

                if (resizable) {
                    // 自缩排更新字体应为 displayFontSize
                    run.styles.fontSize = run.styles.displayFontSize;
                }

                run.styles.fontSize && (run.styles.fontSize /= FONT_SCALE);
                // 处理结尾TextRun
                endParaRPr = this.getEndParaProps(run.styles, text, index, paraEle);
                return run;
            });
            const paraObj: any =  { textRuns };

            const paraStyle: any = DataTransform.getOriginProps(paraEle);
            const paraData = paraStyle.data;

            // 作为样式要删除额外属性
            delete paraStyle.data;

            // eslint-disable-next-line max-len
            const paragraphProps = convertParaStyles({ level: paraStyle.level, style: paraStyle  }, excludeParagraphInheritedData);
            paragraphProps && (paraObj.paragraphProps = paragraphProps);

            if (endParaRPr) {
                paraObj.endParaRPr = endParaRPr;
            }

            this.fixEmptyPara(paraObj);
            if (paraStyle.character) {
                paraObj.paragraphCharacter = { ...paraStyle.character };
            }
            if (paraData?.endParaRPrData) {  // 转换endPara,如果有修改，丢弃endPara
                this.convertEndPara(paraData, paraEle, paraObj);
            }
            return paraObj;
        });
    }

    /**
     * 转换 document 层级的样式，转为 textBodyStyle
     * @param docElement
     * @param excludeInheritedData 为true只保留自身属性，false会保留继承的属性
     * @returns
     */
    public static convertTextBodyStyle(docElement: DocumentElement, excludeInheritedData = true): ITextBodyStyle {
        const filterFunction = excludeInheritedData
            // 需要保留自身属性，过滤出 element[`origin-raw-${key}`] 为 true 的属性
            ? (key: string, value: AlloyNode[keyof AlloyNode], element: AlloyNode) => element[`origin-raw-${key}`]
            // 过滤掉无用的 raw- 开头的标志
            : (key: string) => !key.startsWith('raw-');
        const docStyles = DataTransform.getOriginPropsWithFilter(docElement, filterFunction);
        const textBodyStyle: ITextBodyStyle = {};
        Object.entries(docStyles).forEach(([key, value]) => {
            if (BODY_STYLES_KEYS.includes(key)) {
                set(textBodyStyle, `bodyStyle.${key}`, value);
            } else if (WRAPPER_STYLES_KEYS.includes(key)) {
                set(textBodyStyle, `wrapperStyle.${key}`, value);
            } else {
                textBodyStyle[key] = value;
            }
        });
        return textBodyStyle;
    }

    private needParagraphListUpdate(paragraphList, resizeLevel, slideNum, datetime, searchInfo) {
        return slideNum !== this.slideNum || datetime !== this.datetime || this.resizeLevel !== resizeLevel
            || !this.compare(searchInfo, this.searchInfo) || !this.compare(paragraphList, this.paragraphList);
    }

    /**
     * clone一份paragraphList
     */
    private getClonedParagraphList(): [] {
        return getParagraphList({ paragraphList: this.paragraphList }, false);
    }

    /**
     * 替换段落中搜索到的文字样式
     * @param paragraphList
     */
    private setSearchInPara(paragraphList) {
        let pList = paragraphList;
        if (this.searchInfo?.isSearchingFlag && this.searchInfo?.searchResult) {
            pList = setStyleForPositionedTextInParagraphList(
                pList,
                this.searchInfo.searchResult,
                this.searchInfo.keyword, {
                    backgroundColor: SEARCH_COLOR,
                    isSearchResult: true,
                },
            );
        }
        if (this.searchInfo?.isHighlightFlag && this.searchInfo?.highlightResult) {
            pList = setStyleForPositionedTextInParagraphList(
                pList,
                this.searchInfo.highlightResult,
                this.searchInfo.keyword,
                {
                    backgroundColor: HIGHLIGHT_COLOR,
                    isSearchResult: true,
                },
            );
        }
        return pList;
    }

    /**
     * 深比较两个对象是否相等
     * @param obj1
     * @param obj2
     */
    private compare(obj1, obj2): boolean {
        // 这个是不是直接用 lodash 的方法就行
        return editorCompareEngine.compare(obj1, obj2);
    }

    /**
     * 解析textBody数据
     * @param textBodyStyle
     */
    private parseTextBodyStyle(textBodyStyle?: ITextBodyStyle) {
        this.autoFitType = textBodyStyle?.autoFitType;
        this.resizable = getResiziable(this.autoFitType);

        const { textDirection } = textBodyStyle?.wrapperStyle || {};
        this.textDirection = textDirection || TextDirectionType.HORIZONTAL;
        this.isVert = isVerticalDirection(textDirection);
    }

    private static getEmptyTextrun(endParaRPr, resizeLevel, noRawData?) {
        return {
            text: '',
            styles: combineTextrunStyle({
                textRunStyle: endParaRPr,
                resizeLevel,
                noRawData,
            }),
        };
    }

    private static computeStyles(run, i, paragraph, resizeLevel, noRawData?) {
        let styles = null;
        // 这个是沿用旧的逻辑：如果是runBr的话，继承上一个textrun的属性值；否则给一个最小值，防止高度错误
        let runStyle = run.styles;
        if (run.type === 'runBr') {
            if (i <= 0) {
                runStyle = { fontSize: 6 };
            } else {
                runStyle = paragraph.textRuns[i - 1].styles || { fontSize: 6 };
            }
        }
        styles = combineTextrunStyle({
            textRunStyle: runStyle,
            text: run.text,
            resizeLevel,
            noRawData,
        });
        return styles;
    }

    private static getRunText(run, slideNum, datetime: Date, model?) {
        let { text } = run;
        if (run.type === 'runBr') {
            text = '\n';
        }
        // textrun类型为幻灯片编号，text取当前页码为值
        if (run.fieldType === 'slidenum' && !expect(slideNum).isNullOrUndefined() && !model?.showOriginalText) {
            text = `${slideNum}`;
        }
        // textrun类型为日期与时间，text取当前当前格式的时间
        if (this.isDatetimeType(run)) {
            const time = getFormatTime(run, datetime);
            text = `${time}`;
        }
        return text;
    }

    private static isDatetimeType(run) {
        return run.fieldType && run.fieldType.indexOf('datetime') > -1;
    }

    private static getEndParaData(isParaEmpty, endParaRPr, leaves) {
        let endParaRPrData;
        if (this.hasEndPara(isParaEmpty, endParaRPr, leaves)) {
            const lastTextRun = leaves.length > 0 ? leaves[leaves.length - 1] : null;
            const text = lastTextRun ? lastTextRun.text : '';
            const lastChar = (text && text.length > 0) ? text[text.length - 1] : '';
            endParaRPrData = {  // 后面数据用来对比，有变更就丢弃endPara
                endParaRPr: { ...endParaRPr },
                pLen: leaves.length,
                lastRunLen: text ? text.length : 0,
                lastChar,
            };
        }
        return endParaRPrData;
    }

    private static hasEndPara(isParaEmpty, endParaRPr, leaves) {
        return !isParaEmpty && endParaRPr && leaves;
    }

    private static getEndParaProps(styles, text, index, textNode) {
        let endParaRPr;
        if (styles.useEndParaStyle) {
            if (!text && index === textNode.children.length - 1) { // 尾TextRun没被修改
                endParaRPr = styles;
            } else {
                delete styles.useEndParaStyle;
            }
        }
        return endParaRPr;
    }

    private static fixEmptyPara(paragraph) {
        const para = paragraph;
        let pTextRuns;
        if (Array.isArray(para.textRuns)) {
            // 去除空的textRun
            pTextRuns = para.textRuns.filter(item => item.type === 'runBr' || item.text);
        }
        if (pTextRuns && pTextRuns.length === 0) { // 自己产生的空行,设置endParaRPr并使用空的textRun的样式
            const emptyTextRun = para.textRuns.length > 0 ? para.textRuns[0] : null;
            if (emptyTextRun?.styles) {
                para.endParaRPr = emptyTextRun.styles;
            }
        }
        para.textRuns = pTextRuns;
    }

    private static convertEndPara(node, textNode, paraObj) {
        const endParaData = node.endParaRPrData;
        const { children: leaves } = textNode;
        const lastTextRun = (leaves?.length) > 0 ? leaves[leaves.length - 1] : null;
        const text = lastTextRun ? lastTextRun.text : '';
        const lastChar = (text && text.length > 0) ? text[text.length - 1] : '';
        if (this.shouldConvertEndPara(leaves, endParaData, text, lastChar)) {
            // eslint-disable-next-line no-param-reassign
            paraObj.endParaRPr = { ...endParaData.endParaRPr };
        }
    }

    private static shouldConvertEndPara(leaves, endParaData, text, lastChar) {
        return leaves && endParaData.pLen === leaves.length && endParaData.lastRunLen === (text ? text.length : 0)
            && (endParaData.lastChar === lastChar || (!lastChar && !endParaData.lastChar));
    }
}
